import React from "react";
import { Box, Stack, Typography, Chip, Skeleton } from "@mui/material";
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

const CATEGORY_LABELS: Record<string, string> = {
  relationship: "Relationship",
  pricing: "Pricing",
  logistics: "Logistics",
  documentation: "Documentation",
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  relationship: tokens.chip.indigo,
  pricing: tokens.chip.blue,
  logistics: tokens.chip.amber,
  documentation: tokens.chip.green,
};

interface SupplierQuestionnaireTabProps {
  questions: any[];
  isLoading: boolean;
}

const SupplierQuestionnaireTab: React.FC<SupplierQuestionnaireTabProps> = ({
  questions,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={72} />
        ))}
      </Stack>
    );
  }

  if (questions.length === 0) {
    return (
      <Box
        sx={{
          border: `1px solid ${tokens.border.container}`,
          borderRadius: tokens.radius.md,
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
          No questionnaire data yet. Generate a supplier portal link to initialize the questionnaire.
        </Typography>
      </Box>
    );
  }

  const answeredCount = questions.filter((q) => q.status === "answered").length;
  const totalCount = questions.length;

  return (
    <Box>
      {/* Progress bar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.sm2,
            color: tokens.text.secondary,
          }}
        >
          {answeredCount} of {totalCount} questions answered
        </Typography>
        <Box
          sx={{
            flex: 1,
            maxWidth: 200,
            height: 6,
            bgcolor: tokens.bg.muted,
            borderRadius: tokens.radius.full,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${totalCount > 0 ? (answeredCount / totalCount) * 100 : 0}%`,
              height: "100%",
              bgcolor: tokens.status.success,
              borderRadius: tokens.radius.full,
              transition: "width 300ms ease",
            }}
          />
        </Box>
      </Stack>

      {/* Questions list */}
      <Stack spacing={1.5}>
        {questions.map((q: any) => {
          const catColor = CATEGORY_COLORS[q.category] || tokens.chip.gray;
          const isAnswered = q.status === "answered";
          return (
            <Box
              key={q._id || q.questionId}
              sx={{
                border: `1px solid ${tokens.border.container}`,
                borderRadius: tokens.radius.md,
                p: 2,
                bgcolor: tokens.bg.surface,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {isAnswered ? (
                  <CheckIcon
                    sx={{ fontSize: 18, color: tokens.status.success, mt: 0.25 }}
                  />
                ) : (
                  <PendingIcon
                    sx={{ fontSize: 18, color: tokens.text.muted, mt: 0.25 }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.sm2,
                        fontWeight: tokens.fontWeight.semibold,
                        color: tokens.text.primary,
                      }}
                    >
                      {q.question || q.questionId}
                    </Typography>
                    {q.category && (
                      <Chip
                        label={CATEGORY_LABELS[q.category] || q.category}
                        size="small"
                        sx={{
                          fontSize: tokens.fontSize["2xs"],
                          height: 18,
                          bgcolor: catColor.bg,
                          color: catColor.text,
                          borderRadius: tokens.radius.sm,
                        }}
                      />
                    )}
                  </Stack>
                  {q.answer ? (
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.xs,
                        color: tokens.text.secondary,
                        mt: 0.5,
                      }}
                    >
                      {q.answer}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.xs,
                        color: tokens.text.muted,
                        fontStyle: "italic",
                        mt: 0.5,
                      }}
                    >
                      Awaiting supplier response
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {/* Note about validation integration */}
      <Typography
        sx={{
          fontSize: tokens.fontSize.xs,
          color: tokens.text.muted,
          mt: 2,
          px: 1,
        }}
      >
        Questionnaire responses are automatically included in AI validation context.
      </Typography>
    </Box>
  );
};

export default SupplierQuestionnaireTab;
